import React, { useState } from 'react';
import { Alert, Button, Card, Spinner, Input } from '../../../core/ui';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../core/context/TranslationContext';
import { disbandFamily, leaveFamily, renameFamily } from '../api/family';
import { useFamily } from '../context/FamilyContext';
import InviteList from './InviteList';
import ShareFlagToggle from './ShareFlagToggle';
import styles from './FamilySettings.module.css';

export default function FamilySettings() {
  const { t } = useTranslation();
  const { family, loading, refresh } = useFamily();
  const navigate = useNavigate();
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (loading) return <Spinner label={t('loading')} className={styles.spinner} />;
  if (!family) return <Navigate to="/family/create" replace />;

  const myUsername = localStorage.getItem('username');
  const me = family.members?.find(m => m.username === myUsername) || family.members?.[0];
  const isOwner = me?.role === 'OWNER';

  const handleRename = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      setSaving(true);
      await renameFamily(newName.trim());
      await refresh();
      setNewName('');
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(t('family.leave.confirm'))) return;
    try {
      await leaveFamily();
      await refresh();
      navigate('/family/create');
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    }
  };

  const handleDisband = async () => {
    if (!window.confirm(t('family.disband.confirm'))) return;
    try {
      await disbandFamily();
      await refresh();
      navigate('/family/create');
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    }
  };

  return (
    <div className={styles.container}>
      <h4>{t('family.settings')}</h4>
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className={styles.card}>
        <Card.Body>
          <Card.Title>{t('family.share.tooltip')}</Card.Title>
          {me && <ShareFlagToggle shareReceipts={me.shareReceipts} shareBudget={me.shareBudget} />}
        </Card.Body>
      </Card>

      {isOwner && (
        <Card className={styles.card}>
          <Card.Body>
            <Card.Title>{family.name}</Card.Title>
            <form onSubmit={handleRename} className={styles.renameForm}>
              <Input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('family.create.namePlaceholder')}
              />
              <Button type="submit" variant="secondary" disabled={saving}>
                {t('saveChanges')}
              </Button>
            </form>
          </Card.Body>
        </Card>
      )}

      {isOwner && (
        <Card className={styles.card}>
          <Card.Body>
            <Card.Title>{t('family.invite.create')}</Card.Title>
            <InviteList />
          </Card.Body>
        </Card>
      )}

      <Card className={styles.card}>
        <Card.Body className={styles.actionRow}>
          {!isOwner && (
            <Button variant="secondary" onClick={handleLeave}>{t('family.leave')}</Button>
          )}
          {isOwner && (
            <Button variant="dangerGhost" onClick={handleDisband}>{t('family.disband')}</Button>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
